import { z } from 'zod';

import { orderStatusSchema } from '@/features/orders';
import { enforceRangePairs } from '@/lib/api/filter-spec';
import { pageMetaSchema } from '@/lib/api/pagination';
import { emailAddressSchema } from '@/lib/validation/email';
import { usPhoneSchema } from '@/lib/validation/phone';

/** The three Customer account states (CONTEXT.md — "Customer status"). Wire token is
 *  `pending_activation` (deployed orders customer-status meta; aligned in feature 102099). */
export const customerStatusSchema = z.enum(['active', 'inactive', 'pending_activation']);

/** One Customer row on the listing. There is no `avatarUrl` field at all on the wire (the row
 *  renders an initials fallback); `email` is nullable (a customer may have none on file). */
export const customerRowSchema = z.object({
  id: z.string(),
  customerId: z.string(),
  name: z.string(),
  email: z.string().nullable(),
  phone: z.string(),
  deviceCount: z.number(),
  status: customerStatusSchema,
});

/** One postal address on a Customer — the real wire shape (`CustomerAddressData`), shared by the
 *  Customer Detail header and the Edit-prefill read path. `isDefault` marks the primary address;
 *  `addresses[]` is returned default-first so no client-side reordering is needed. */
export const customerAddressSchema = z.object({
  id: z.string(),
  title: z.string(),
  line1: z.string(),
  line2: z.string().nullable().optional(),
  city: z.string(),
  state: z.string(),
  zipcode: z.string(),
  isDefault: z.boolean().optional(),
});

/** Customer Detail header payload — `customerRowSchema` + contact addresses + order/address
 *  counts. `additionalAddressCount` (`addresses.length - 1`, floored at 0) drives the "2+" badge;
 *  the backend computes it so the FE never has to derive it from the array. */
export const customerDetailSchema = customerRowSchema.extend({
  totalOrderCount: z.number().int().nonnegative(),
  additionalAddressCount: z.number().int().nonnegative().default(0),
  addresses: z.array(customerAddressSchema).default([]),
});

export const customerListResponseSchema = z.object({
  data: z.array(customerRowSchema),
  meta: pageMetaSchema,
});

/** The 9 FE-owned Activity Log Type values (README AC). The event→Type→description catalog is
 *  backend-owned; the FE owns only these display values (this table + ticket 09's filter). */
export const customerActivityTypeSchema = z.enum([
  'device',
  'installation',
  'subscription',
  'warranty',
  'replacement',
  'order',
  'profile',
  'status',
  'support_ticket',
]);

/** One Activity Log audit entry on the Customer Detail tab. Display-only — generated by the
 *  backend. `logBy` is who/what caused the event: `name` + `isSystem` (a system-triggered entry,
 *  e.g. the daily subscription-expiry sweep). There is no `avatarUrl` field on the wire — the
 *  column renders an initials fallback. `loggedAt` is a UTC instant rendered per
 *  `formatActivityLogDateTime` (ADR-0007). */
export const customerActivityLogRowSchema = z.object({
  id: z.string(),
  logBy: z.object({ name: z.string(), isSystem: z.boolean() }),
  type: customerActivityTypeSchema,
  loggedAt: z.string(),
  description: z.string(),
});

export const customerActivityLogListResponseSchema = z.object({
  data: z.array(customerActivityLogRowSchema),
  meta: pageMetaSchema,
});

/** One Order row on the customer's Order History tab. `orderDate` is a UTC instant, null before
 *  the order is placed (a pre-placement order, feature 122355);
 *  `deliveryDate` is a nullable Eastern date-only string, null when not yet delivered
 *  (ADR-0007). `paymentMethod` is nullable for the same reason ADO 118734 made the Order
 *  Detail page's nullable: a $0.00 **replacement** order carries no Stripe charge, so the
 *  backend sends null. Requiring a string threw for the whole list and `<DataTable>` has no
 *  error branch, so one such order emptied the entire tab (ADO 119623). */
export const orderHistoryRowSchema = z.object({
  id: z.string(),
  orderNumber: z.string(),
  orderDate: z.string().nullable(),
  deliveryDate: z.string().nullable(),
  orderStatus: orderStatusSchema,
  orderAmount: z.number(),
  paymentMethod: z.string().nullable(),
  itemCount: z.number().int().nonnegative(),
});

export const orderHistoryListResponseSchema = z.object({
  data: z.array(orderHistoryRowSchema),
  meta: pageMetaSchema,
});

/** RHF-draft validator for the filter drawer (ticket 03). Device Range is integer/min-0 (enforced
 *  by the `<NumberField>` `min={0}`/`precision={0}` — Customers may own 0 Devices, so unlike Orders
 *  there is no `>=1` rule); To >= From via the shared range refine. */
export const customerFiltersSchema = z
  .object({
    statuses: z.array(customerStatusSchema),
    deviceCountFrom: z.number().nullable(),
    deviceCountTo: z.number().nullable(),
  })
  .superRefine(enforceRangePairs([['deviceCountFrom', 'deviceCountTo']]));

/** Activity Log filter draft (ticket 09). `type` is a multi-select (∈ set); date/time bounds are
 *  strings; From≤To enforced per range. Mirrors the subscription filter schema (custom superRefine,
 *  not `enforceRangePairs` — that helper only compares numbers). */
export const customerActivityLogFiltersSchema = z
  .object({
    type: z.array(customerActivityTypeSchema),
    logDateFrom: z.string().nullable(),
    logDateTo: z.string().nullable(),
    logTimeFrom: z.string().nullable(),
    logTimeTo: z.string().nullable(),
  })
  .superRefine((v, ctx) => {
    if (v.logDateFrom && v.logDateTo && v.logDateFrom > v.logDateTo) {
      ctx.addIssue({
        code: 'custom',
        path: ['logDateTo'],
        message: 'End date must be after start date',
      });
    }
    if (v.logTimeFrom && v.logTimeTo && v.logTimeFrom > v.logTimeTo) {
      ctx.addIssue({
        code: 'custom',
        path: ['logTimeTo'],
        message: 'End time must be after start time',
      });
    }
  });
/** Subscription Status — `active` or `expired` (ADO 112576 renamed the earlier `inactive` to
 *  `expired`). Shared by the Subscription Details tab and the Devices tab. */
export const customerSubscriptionStatusSchema = z.enum(['active', 'expired']);

/** One Subscription period for one Device on the customer's Subscription Details tab — active,
 *  expired/past, or a renewal (its own row). `deviceId` scopes the row to one physical device
 *  (also the Subscriptions filter's `deviceId` value). `deviceType` mirrors the Devices module's
 *  three values (`formatDeviceType` renders these). `endDate` is null for an ongoing period. Dates
 *  are UTC instants rendered per `formatDeliveryDate` (ADR-0007). */
export const customerSubscriptionRowSchema = z.object({
  id: z.string(),
  deviceId: z.string(),
  deviceName: z.string(),
  deviceType: z.enum(['dm_device', 'cropx', 'other']),
  startDate: z.string(),
  endDate: z.string().nullable(),
  subscriptionStatus: customerSubscriptionStatusSchema,
});

export const customerSubscriptionListResponseSchema = z.object({
  data: z.array(customerSubscriptionRowSchema),
  meta: pageMetaSchema,
});

/** RHF-draft validator for the Subscription Details filter drawer (ticket 07). `deviceId` is a
 *  multi-select (the backend has no device-name meta endpoint, so options are built client-side
 *  from the current page's rows — see `useCurrentPageDeviceOptions`-style derivation in the
 *  page). Other selects clear to `null`; date bounds are nullable ISO strings. Each range's To
 *  must not precede its From — the single `<DateRangePicker>` already prevents this, but the
 *  refine guards it and drives Apply-disable. */
export const customerSubscriptionFiltersSchema = z
  .object({
    deviceId: z.array(z.string()),
    subscriptionStatus: customerSubscriptionStatusSchema.nullish(),
    deviceType: z.enum(['dm_device', 'cropx', 'other']).nullish(),
    startDateFrom: z.string().nullable(),
    startDateTo: z.string().nullable(),
    endDateFrom: z.string().nullable(),
    endDateTo: z.string().nullable(),
  })
  .superRefine((v, ctx) => {
    if (v.startDateFrom && v.startDateTo && v.startDateFrom > v.startDateTo) {
      ctx.addIssue({
        code: 'custom',
        path: ['startDateTo'],
        message: 'End date must be after start date',
      });
    }
    if (v.endDateFrom && v.endDateTo && v.endDateFrom > v.endDateTo) {
      ctx.addIssue({
        code: 'custom',
        path: ['endDateTo'],
        message: 'End date must be after start date',
      });
    }
  });

/** Device Type on the customer Devices tab — same three values as the Devices module
 *  (`formatDeviceType` renders these). */
export const customerDeviceTypeSchema = z.enum(['dm_device', 'cropx', 'other']);

/** Device Status — the six ADO 112576 states (renamed from the earlier five-value
 *  active/idle set: Active → Running, Idle → Off, plus the new Pending Confirmation).
 *  `pending_confirmation` is a state a device genuinely rests in: `param-templates/set` leaves it
 *  there and only the separate confirm call advances it to Pending QC, so a confirmation that fails
 *  leaves a device an admin just configured in this state (task 114813). */
export const customerDeviceStatusSchema = z.enum([
  'pending_configuration',
  'pending_confirmation',
  'pending_qc',
  'running',
  'off',
  'disconnected',
]);

/** Connector Status — the outcome of a Device's connector setup, tracked internally by the
 *  backend and independent of Device Status (story 113560). Exactly three values, never null and
 *  never absent: the backend enum is `pending | connected | failed`
 *  (`greenway_api` `libs/modules/src/subscription/constants/subscription.constants.ts`). */
export const customerConnectorStatusSchema = z.enum(['pending', 'connected', 'failed']);

/** One subscription-bearing Device UNIT on the customer's Devices tab. `category` is a bare
 *  category name (no master Category endpoint exists yet — TODO(device-category-meta) on the
 *  backend), `serialNo` and `lastActive` are nullable until device provisioning ships. */
export const customerDeviceRowSchema = z.object({
  id: z.string(),
  category: z.string(),
  deviceName: z.string(),
  deviceType: customerDeviceTypeSchema,
  serialNo: z.string().nullable(),
  deviceStatus: customerDeviceStatusSchema,
  lastActive: z.string().nullable(),
  connectorStatus: customerConnectorStatusSchema,
  subscriptionStatus: customerSubscriptionStatusSchema,
});

export const customerDeviceListResponseSchema = z.object({
  data: z.array(customerDeviceRowSchema),
  meta: pageMetaSchema,
});

/** RHF-draft validator for the Devices filter drawer. `category` is a single-select built
 *  client-side from the current page's rows (no master Category endpoint exists — the backend's
 *  own TODO confirms this is the intended interim approach); there is no `deviceName` filter
 *  param on this tab (the search box already matches Device Name + Serial No). */
export const customerDeviceFiltersSchema = z.object({
  // `.nullish()` (null | undefined | value): an antd `allowClear` Select fires `onChange(undefined)`
  // on clear, while a re-seeded field may be `null` — the draft must accept both as "unselected".
  category: z.string().nullish(),
  // AC 102104 lists Device Type as the second dropdown and requires it to filter the table; it was
  // missing from the drawer and the spec entirely (ADO 119225). Reuses the row schema's own enum
  // so a value the table can render is exactly a value the filter can select.
  deviceType: customerDeviceTypeSchema.nullish(),
  deviceStatus: customerDeviceStatusSchema.nullish(),
  // Story 113560 Section B: a fifth dropdown beside the existing four, AND-combined with them.
  connectorStatus: customerConnectorStatusSchema.nullish(),
  subscriptionStatus: customerSubscriptionStatusSchema.nullish(),
});

/** RHF-draft validator for the Order History filter drawer. Field names match the real
 *  `GetCustomerOrdersDto` params (`orderStatus`, `orderDateFrom/To`, `deliveryDateFrom/To`).
 *  Numeric To >= From via the shared range refine; each date range guards To >= From with the
 *  AC copy "End date must be after start date". */
export const orderHistoryFiltersSchema = z
  .object({
    orderStatus: z.array(orderStatusSchema),
    paymentMethods: z.array(z.string()),
    orderDateFrom: z.string().nullable(),
    orderDateTo: z.string().nullable(),
    deliveryDateFrom: z.string().nullable(),
    deliveryDateTo: z.string().nullable(),
    amountFrom: z.number().nullable(),
    amountTo: z.number().nullable(),
    itemsFrom: z.number().nullable(),
    itemsTo: z.number().nullable(),
  })
  .superRefine(
    enforceRangePairs([
      ['amountFrom', 'amountTo'],
      ['itemsFrom', 'itemsTo'],
    ]),
  )
  .superRefine((v, ctx) => {
    for (const [from, to] of [
      ['orderDateFrom', 'orderDateTo'],
      ['deliveryDateFrom', 'deliveryDateTo'],
    ] as const) {
      const a = v[from];
      const b = v[to];
      if (a && b && a > b) {
        ctx.addIssue({ code: 'custom', path: [to], message: 'End date must be after start date' });
      }
    }
  });

/** One postal address on the customer write body — mirrors the deployed webstore `CreateAddressDto`
 *  (grilling Q3). `state` is the **full name** the ZIP lookup returns (read-only, auto-populated —
 *  ticket 113129; full name since ADO 120879). `city` is a ZIP-driven dropdown (options from `zipLookupSchema`);
 *  the zip↔city↔state relationship is validated by the backend on submit, not here. `title` maxLength
 *  is **50** (grilling Revision 2 — BE `varchar(50)` matching the AC; only the retired webstore DTO
 *  capped at 20). Text fields are trimmed so whitespace-only input can't satisfy `min()`. */
/**
 * A blank optional string is **omitted** from the payload, never sent as `''`.
 *
 * class-validator's `@IsOptional()` skips `undefined`/`null` but **not** the empty string, so an
 * untouched optional field reached the format/length rule behind it and the save failed —
 * `@IsEmail()` for Email (ADO 118854), `@Length(2, 100)` for Address Line 2 (ADO 118788). Both ACs
 * say a blank optional field is simply not an error, so the client omits the key: `JSON.stringify`
 * drops an `undefined` value, which is exactly what the backend wants to see.
 *
 * Applied at the schema, not per call site, so any optional string added later inherits it.
 */
const blankToUndefined = (value: unknown) =>
  typeof value === 'string' && value.trim() === '' ? undefined : value;

export const addressWriteSchema = z.object({
  id: z.string().optional(),
  // AC 102101 "Address Title Validation" / server-errors.ts `serverWouldRejectTitle`: the backend's
  // own `@Length(2, 50)` rejection reads "Enter a valid title" — this must match, both empty and
  // 1-char (ADO 118801, which had the required-field copy here instead).
  title: z.string().trim().min(2, 'Enter a valid title').max(50),
  line1: z.string().trim().min(2, 'Enter a valid address').max(100),
  // AC 102101: "format validation only applies if text is entered" — blank is omitted above, and
  // anything shorter than 2 characters is the AC's own `Enter a valid address` (matching the
  // backend's `@Length(2, 100)`, which the old no-min rule left to the server to discover).
  line2: z.preprocess(
    blankToUndefined,
    z.string().trim().min(2, 'Enter a valid address').max(100).optional(),
  ),
  city: z.string().trim().min(1, 'Enter a city'),
  state: z.string().trim().min(1, 'Select a state').max(50, 'Select a state'),
  zipcode: z.string().regex(/^\d{5}$/, 'Enter a valid 5-digit US Zipcode'),
  isDefault: z.boolean().optional(),
});

/** Customer create/update body (spec Contract). Names accept letters + spaces only (AC — input
 *  masking strips the rest in ticket 02/03; this is the submit gate), capped at 50; phone is a bare
 *  10-digit US (NANP) number — on edit, `storedPhone` lets an unchanged pre-rule number through
 *  (ADR-0035); email is optional but format-checked when present. Addresses are optional
 *  (0..n) — `isDefault` is derived (index 0) by the UI, never required here. */
export const buildCustomerWriteSchema = (storedPhone?: string) =>
  z
    .object({
      firstName: z
        .string()
        .trim()
        .min(1, 'This field is required')
        .max(50)
        .regex(/^[A-Za-z ]+$/, 'This field is required'),
      lastName: z
        .string()
        .trim()
        .min(1, 'This field is required')
        .max(50)
        .regex(/^[A-Za-z ]+$/, 'This field is required'),
      phone: usPhoneSchema('Enter a valid 10-digit US phone number', storedPhone),
      // Shared rule, not `z.email()` — see `@/lib/validation/email` (ADO 118281): the built-in
      // refuses fifteen RFC 5322 specials this endpoint's own `@IsEmail()` accepts.
      email: z.preprocess(
        blankToUndefined,
        emailAddressSchema('Enter a valid email address').optional(),
      ),
      addresses: z.array(addressWriteSchema).default([]),
    })
    /**
     * Address names must be unique within one Customer (AC 102101), case-insensitively — the same
     * comparison the backend's `@ArrayUnique((a) => a.title?.toLowerCase())` makes.
     *
     * Checked here rather than only on the server because the AC asks for an **inline** error on the
     * offending block, and the server cannot say which block that is: the `exceptionFactory`
     * (`setup-app.ts`) flattens every failure to `field: error.property`, so an `@ArrayUnique`
     * rejection arrives as a bare `addresses` with the array index dropped. Owning it here also
     * disables Create before any request is made (ADO 118807).
     *
     * The issue lands on the **later** duplicate: the first block keeps its name and the admin
     * renames the one they just added.
     */
    .superRefine((value, ctx) => {
      const seen = new Set<string>();
      value.addresses?.forEach((address, index) => {
        const key = address.title?.trim().toLowerCase();
        if (!key) return;
        if (seen.has(key)) {
          ctx.addIssue({
            code: 'custom',
            path: ['addresses', index, 'title'],
            message: 'Address name already exists for this customer',
          });
          return;
        }
        seen.add(key);
      });
    });

export const customerWriteSchema = buildCustomerWriteSchema();

/** ZIP-lookup response (ticket 113129) — `GET /customers/admin/address/zip-lookup?zipcode={5}`.
 *  `state` is the full State name (`Texas`, not `TX` — ADO 120879); `city` is the primary city; `cities` are the City dropdown
 *  options for that ZIP. Powers auto-population; submit stays server-validated (the 422 codes). */
export const zipLookupSchema = z.object({
  state: z.string(),
  city: z.string(),
  cities: z.array(z.string()),
});

/** Read-side address shape: the same fields as `addressWriteSchema` but with **no** create-time
 *  constraints (lengths, 5-digit zip, 2-letter state). Reading and writing are different jobs — a
 *  legacy row that predates those rules must still load so the admin can *fix* it. Parsing the GET
 *  with the strict write schema instead bricked the whole Edit page ("This customer could not be
 *  loaded") for any record the backend happily stores. Validation still happens on submit, via
 *  `customerWriteSchema`, which surfaces the offending field inline. */
export const addressEditReadSchema = z.object({
  id: z.string().optional(),
  title: z.string(),
  line1: z.string(),
  line2: z.string().nullable().optional(),
  city: z.string(),
  state: z.string(),
  zipcode: z.string(),
  isDefault: z.boolean().optional(),
});

/** Edit-prefill read shape (grilling Q3 / ticket 01 decision #3). `GET /customers/admin/:id`
 *  returns the full-address SUPERSET; this parses the fields the Edit form needs and drops the
 *  display-only counts (`additionalAddressCount`) the 102098 detail hook consumes. Addresses use
 *  the **lenient** `addressEditReadSchema` so the form can load-then-correct rather than refusing
 *  to open. */
export const customerEditSchema = z.object({
  firstName: z.string(),
  lastName: z.string(),
  phone: z.string(),
  email: z.string().nullable(),
  addresses: z.array(addressEditReadSchema),
});

/** Maps the parsed edit detail to `<CustomerForm>` `defaultValues`. Phone is normalized to a bare
 *  10-digit number (the form's `PhoneField` masks/renders the `+1` prefix; the BE/display form may
 *  carry `+1 (…) …`); addresses pass through with their `id` (PUT is a full replace, each existing
 *  address may carry an id — spec Contract). `isDefault` stays derived (index 0), never a control. */
export function toCustomerWriteDefaults(
  detail: z.infer<typeof customerEditSchema>,
): Partial<z.infer<typeof customerWriteSchema>> {
  return {
    firstName: detail.firstName,
    lastName: detail.lastName,
    phone: detail.phone.replace(/\D/g, '').slice(-10),
    email: detail.email ?? '',
    addresses: detail.addresses.map((a) => ({ ...a, line2: a.line2 ?? undefined })),
  };
}

/** Letters + spaces only, capped at 50 (AC — masked as typed; the schema is the submit gate).
 *  Leading spaces are dropped so a name can't be started with whitespace; interior/trailing spaces
 *  stay so "Mary Jane" is typable. Whitespace-only input therefore masks to '' and fails `min(1)`. */
export function maskCustomerName(raw: string): string {
  return raw
    .replace(/[^A-Za-z ]/g, '')
    .replace(/^ +/, '')
    .slice(0, 50);
}
/** Digits only, capped at 5 (US zip format). */
export function maskZip(raw: string): string {
  return raw.replace(/\D/g, '').slice(0, 5);
}
